import type { SkillTypeDto } from "@ckweb/ckfetch/connectables/employeeManagement";
import type { EquipAreaViewDto } from "@ckweb/ckfetch/connectables/equipmentAreaManagement";
import type { GridConfig } from "@/types/grid";
import type { BulkImportPageSetupStatic } from "@/types/bulk-import";

export function getTaskListPageConfig(): BulkImportPageSetupStatic<TaskListSchema> {
  return {
    id: "task-list",
    pageTitle: "cksetup.imports.task-list.pageTitle",
    pageHeading: "cksetup.imports.task-list.heading",
    learnMoreTitle: "cksetup.imports.task-list.learnMoreTitle",
    learnMoreText: "cksetup.imports.task-list.learnMoreText",
    templateFileName: "cksetup.imports.task-list.templateName",
    templateWorksheetName: "tableColumns.taskLists.worksheetName",
    tabTitle: "cksetup.imports.bulk.taskListHeading",
    tabDescription: "cksetup.imports.bulk.taskListDescription",
    imageSrc: "/static/images/bulk/task-list.png",
    tabTooltip: "cksetup.imports.bulk.taskListTooltip",
    addButtonLabel: "buttons.addNewTask",
    validationSchema: taskListSchema,
  };
}

export async function getTaskListGridConfig({
  equipmentAreas,
  skillTypes,
}: {
  equipmentAreas: EquipAreaViewDto[];
  skillTypes: SkillTypeDto[];
}): Promise<GridConfig<TaskListSchema>> {
  const defaultSkillLevel = skillTypes.find(({ isDefault }) => isDefault);
  const defaultRowData: Partial<TaskListSchema> = {
    calculationMethod: "M",
    priority: 2,
    forecastMinutes: 0,
    totalIntervalInWeeks: 1,
    offsetWeeks: 0,
    showCompleted: true,
    controlPoint: false,
    isSubtask: false,
    skillLevel: defaultSkillLevel?.code,
    item2: "",
  };
  return {
    computeItemFn: (dataItem, { rowIndex, gridData }) => {
      // make sure first task is not a subtask
      const itemRowIndex = rowIndex ?? gridData.length;
      dataItem.isSubtask = itemRowIndex > 0 ? dataItem.isSubtask : false;
      return dataItem;
    },
    defaultRowData,
    columns: [
      {
        field: "isSubtask",
        title: "tableColumns.taskLists.isSubtaskHeader",
        helperText: "tableColumns.taskLists.isSubtaskHelperText",
        editor: "boolean",
        minWidthPx: 180,
        alternativeMappingNames: ["Task/Subtask"],
      },
      {
        field: "equipmentAreaId",
        title: "tableColumns.taskLists.equipmentArea",
        helperText: "tableColumns.taskLists.equipmentAreaHelperText",
        placeholder: "tableColumns.select",
        editor: "select",
        editorProps: {
          items: equipmentAreas
            .filter(({ active }) => active)
            .map((item) => ({
              label: item.name,
              value: item.number,
            })),
        },
        minWidthPx: 180,
        alternativeMappingNames: ["Equipment Area"],
      },
      {
        field: "taskDescription",
        title: "tableColumns.taskLists.taskDescription",
        helperText: "tableColumns.taskLists.taskDescriptionHelperText",
        editor: "text",
        placeholder: "tableColumns.taskLists.taskDescriptionPlaceholder",
        minWidthPx: 250,
      },
      {
        field: "bilingualTaskDescription",
        title: "tableColumns.taskLists.taskDescriptionBilingual",
        helperText: "tableColumns.taskLists.taskDescriptionBilingualHelperText",
        editor: "text",
        placeholder:
          "tableColumns.taskLists.taskDescriptionBilingualPlaceholder",
        required: false,
        minWidthPx: 250,
      },
      {
        field: "forecastMinutes",
        title: "tableColumns.taskLists.forecastMinutes",
        helperText: "tableColumns.taskLists.forcastMinutesHelperText",
        editor: "numeric",
        placeholder: "tableColumns.taskLists.forcastMinutesPlaceholder",
        minWidthPx: 200,
        editorProps: {
          min: 0,
          max: 32768,
        },
      },
      {
        field: "showCompleted",
        title: "tableColumns.taskLists.showCompleted",
        helperText: "tableColumns.taskLists.showCompletedHelperText",
        editor: "boolean",
        minWidthPx: 180,
      },
      {
        field: "canPerformWhileRunning",
        title: "tableColumns.taskLists.canPerformWhileRunning",
        helperText: "tableColumns.taskLists.canPerformWhileRunningHelperText",
        editor: "boolean",
        minWidthPx: 250,
      },
      {
        field: "calculationMethod",
        title: "tableColumns.taskLists.calculationMethod",
        helperText: "tableColumns.taskLists.calculationMethodHelperText",
        editor: "select",
        placeholder: "tableColumns.select",
        editorProps: {
          translateItems: true,
          items: [
            {
              value: "M",
              label: "tableColumns.taskLists.calculationMethodMasterSchedule",
            },
            {
              value: "L",
              label: "tableColumns.taskLists.calculationMethodLastDone",
            },
          ],
        },
        minWidthPx: 200,
      },
      {
        field: "skillLevel",
        title: "tableColumns.taskLists.skillLevel",
        helperText: "tableColumns.taskLists.skillLevelHelperText",
        placeholder: "tableColumns.select",
        editor: "select",
        editorProps: {
          items: skillTypes
            // .filter(({ active }) => active) // TODO: all of them are inactive for some reason (BE)
            .map((item) => ({
              label: item.name,
              value: item.code,
            })),
        },
        minWidthPx: 140,
      },
      {
        field: "priority",
        title: "tableColumns.taskLists.priority",
        helperText: "tableColumns.taskLists.priorityHelperText",
        editor: "select",
        placeholder: "tableColumns.select",
        editorProps: {
          items: [1, 2, 3, 4],
        },
      },
      {
        field: "controlPoint",
        title: "tableColumns.taskLists.controlPoint",
        helperText: "tableColumns.taskLists.controlPointHelperText",
        editor: "boolean",
        minWidthPx: 200,
      },
      {
        field: "totalIntervalInWeeks",
        title: "tableColumns.taskLists.totalIntervalInWeeks",
        helperText: "tableColumns.taskLists.totalIntervalInWeeksHelperText",
        editor: "numeric",
        placeholder: "tableColumns.enterNumber",
        minWidthPx: 210,
      },
      {
        field: "offsetWeeks",
        title: "tableColumns.taskLists.offsetWeeks",
        helperText: "tableColumns.taskLists.offsetWeeksHelperText",
        editor: "numeric",
        placeholder: "tableColumns.enterNumber",
        minWidthPx: 160,
      },
    ],
  };
}
